import React, { useMemo } from 'react';
import { Card } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { FileText, AlertCircle, CheckCircle } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';

const DocumentPreview = ({ projectData }) => {
  const { t } = useLanguage();
  
  // Calculate document structure with auto-pagination
  const documentStructure = useMemo(() => {
    return calculateDocumentPages(projectData);
  }, [projectData]);
  
  return (
    <Card className="sticky top-24 border border-slate-200 dark:border-slate-700 bg-white">
      <div className="p-4 border-b border-slate-200 dark:border-slate-700">
        <h3 className="font-semibold text-slate-900 dark:text-white flex items-center">
          <FileText className="h-5 w-5 mr-2 text-blue-600" />
          {t('documentPreview')}
        </h3>
        <p className="text-xs text-slate-600 dark:text-slate-400 mt-1">
          {t('autoPaginationPreview')}
        </p>
      </div>
      
      <ScrollArea className="h-[600px]">
        <div className="p-4 space-y-2">
          {documentStructure.pages.length === 0 ? (
            <div className="text-center py-8">
              <FileText className="mx-auto h-12 w-12 text-slate-400 mb-2" />
              <p className="text-sm text-slate-600 dark:text-slate-400">
                {t('startFillingDetails')}
              </p>
            </div>
          ) : (
            <>
              {documentStructure.pages.map((page, index) => (
                <PreviewPage key={index} page={page} index={index} />
              ))}
              
              {/* Summary */}
              <Card className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-slate-200 dark:border-slate-700 mt-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <CheckCircle className="h-5 w-5 text-green-600" />
                    <span className="text-sm font-medium text-slate-900 dark:text-white">
                      {t('totalPages')}: {documentStructure.totalPages}
                    </span>
                  </div>
                  {documentStructure.warnings > 0 && (
                    <div className="flex items-center space-x-1 text-amber-600">
                      <AlertCircle className="h-4 w-4" />
                      <span className="text-xs">{documentStructure.warnings} {t('warnings')}</span>
                    </div>
                  )}
                </div>
              </Card>
            </>
          )}
        </div>
      </ScrollArea>
    </Card>
  );
};

const PreviewPage = ({ page, index }) => {
  const { t } = useLanguage();
  
  const getPageColor = (status) => {
    switch (status) {
      case 'full':
        return 'border border-slate-200 dark:border-slate-700 bg-green-50 dark:bg-green-900/20';
      case 'overflow':
        return 'border border-slate-200 dark:border-slate-700 bg-amber-50 dark:bg-amber-900/20';
      case 'partial':
        return 'border border-slate-200 dark:border-slate-700 bg-blue-50 dark:bg-blue-900/20';
      default:
        return 'border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800';
    }
  };
  
  const getStatusIcon = (status) => {
    switch (status) {
      case 'full':
        return <CheckCircle className="h-4 w-4 text-green-600" />;
      case 'overflow':
        return <AlertCircle className="h-4 w-4 text-amber-600" />;
      default:
        return <FileText className="h-4 w-4 text-blue-600" />;
    }
  };
  
  return (
    <Card className={`p-3 ${getPageColor(page.status)}`}>
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center space-x-2">
          {getStatusIcon(page.status)}
          <span className="font-mono text-sm font-semibold text-slate-900 dark:text-white">
            {page.sheetNumber}
          </span>
        </div>
        <span className="text-xs text-slate-600 dark:text-slate-400">
          {page.capacity}% {t('full')}
        </span>
      </div>
      
      <h4 className="text-sm font-medium text-slate-900 dark:text-white mb-2">
        {page.title}
      </h4>
      
      <div className="space-y-1">
        {page.contentBlocks.map((block, idx) => (
          <div key={idx} className="flex items-center space-x-2 text-xs">
            <div className="w-2 h-2 rounded-full bg-blue-400"></div>
            <span className="text-slate-600 dark:text-slate-400">{block.title}</span>
            {block.continued && (
              <span className="text-amber-600 text-xs italic">({t('continued')})</span>
            )}
          </div>
        ))}
      </div>
      
      {page.overflow && (
        <div className="mt-2 pt-2 border-t border-amber-300">
          <p className="text-xs text-amber-700 dark:text-amber-400 flex items-center">
            <AlertCircle className="h-3 w-3 mr-1" />
            {t('contentWillContinue')} {page.overflowTo}
          </p>
        </div>
      )}
    </Card>
  );
};

// Smart pagination calculator
const calculateDocumentPages = (projectData) => {
  const pages = [];
  let warnings = 0;
  
  if (!projectData.sheets || projectData.sheets.length === 0) {
    return { pages: [], totalPages: 0, warnings: 0 };
  }
  
  const includedSheets = projectData.sheets.filter(s => s.included);
  
  // Cover Sheet (G0.00)
  const coverSheet = includedSheets.find(s => s.sheet_number === 'G0.00');
  if (coverSheet) {
    const coverBlocks = [
      { title: 'Project Information', height: 15 },
      { title: 'Sheet Index', height: 30 },
      { title: 'Vicinity Map', height: 20 },
      { title: 'Building Summary', height: 15 },
    ];
    
    if (projectData.scope_of_work) {
      coverBlocks.push({ title: 'Scope of Work', height: 10 });
    }
    
    const totalHeight = coverBlocks.reduce((sum, b) => sum + b.height, 0);
    
    if (totalHeight > 90) {
      // Need continuation page
      pages.push({
        sheetNumber: 'G0.00',
        title: 'COVER SHEET',
        contentBlocks: coverBlocks.slice(0, 3),
        capacity: 90,
        status: 'full',
        overflow: true,
        overflowTo: 'G0.01'
      });
      
      pages.push({
        sheetNumber: 'G0.01',
        title: 'COVER SHEET (CONTINUED)',
        contentBlocks: coverBlocks.slice(3).map(b => ({ ...b, continued: true })),
        capacity: 60,
        status: 'partial',
      });
      
      warnings++;
    } else {
      pages.push({
        sheetNumber: 'G0.00',
        title: 'COVER SHEET',
        contentBlocks: coverBlocks,
        capacity: Math.min(totalHeight, 90),
        status: 'full',
      });
    }
  }
  
  // Renders
  const renderSheet = includedSheets.find(s => s.sheet_number === 'G0.20');
  if (renderSheet && projectData.renders && projectData.renders.length > 0) {
    const rendersPerPage = 4;
    const totalRenderPages = Math.ceil(projectData.renders.length / rendersPerPage);
    
    for (let i = 0; i < totalRenderPages; i++) {
      const sheetNum = i === 0 ? 'G0.20' : `G0.2${i}`;
      const renderCount = Math.min(rendersPerPage, projectData.renders.length - (i * rendersPerPage));
      
      pages.push({
        sheetNumber: sheetNum,
        title: totalRenderPages > 1 ? `RENDERINGS (${i + 1} of ${totalRenderPages})` : 'RENDERINGS',
        contentBlocks: Array(renderCount).fill(null).map((_, idx) => ({
          title: `Render ${i * rendersPerPage + idx + 1}`
        })),
        capacity: (renderCount / rendersPerPage) * 100,
        status: 'full',
      });
    }
  }
  
  // Add other sheets
  includedSheets
    .filter(s => !['G0.00', 'G0.20'].includes(s.sheet_number))
    .forEach(sheet => {
      pages.push({
        sheetNumber: sheet.sheet_number,
        title: sheet.title,
        contentBlocks: [{ title: 'Content' }],
        capacity: 70,
        status: 'partial',
      });
    });
  
  return {
    pages,
    totalPages: pages.length,
    warnings
  };
};

export default DocumentPreview;